/**
 * useV360Features — Hook que carrega os feature gates da empresa ativa.
 * Fase 9.04 (FASE C).
 *
 * Uso:
 *   const { features, nivel, isLoading, isDefault, overrideNivel, setOverrideNivel } = useV360Features();
 *   const e3 = features.E3?.status;   // "ATIVO" | "PARCIAL" | "DESABILITADO"
 *
 * A empresa vem do TenantContext. Um override manual (?nivel=N1 na URL) é
 * respeitado — útil para o admin/QA simular níveis diferentes.
 */
import { useEffect, useState, useMemo, useCallback, useRef, createContext, useContext } from "react";
import { useLocation } from "react-router-dom";
import { useTenant } from "@/context/TenantContext";
import { v360FeaturesAPI } from "@/services/api";

const V360FeaturesContext = createContext(null);

const EMPTY = { features: {}, nivel: null, is_default: true, loading: true, error: null };


/** Provider — coloca no topo da árvore V360 para cachear a resposta. */
export function V360FeaturesProvider({ children }) {
  const { empresaAtual } = useTenant();
  const location = useLocation();
  const empresaId = empresaAtual?.__live ? empresaAtual.id : null;

  // ?nivel=N1|N2|N3 na URL força override (útil para QA/admin)
  const overrideFromUrl = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const v = (sp.get("nivel") || "").toUpperCase();
    return ["N1", "N2", "N3"].includes(v) ? v : null;
  }, [location.search]);

  const [state, setState] = useState(EMPTY);
  const [manualOverride, setManualOverride] = useState(null);
  const overrideNivel = manualOverride || overrideFromUrl;
  const inFlight = useRef(null);

  const load = useCallback(async () => {
    // Aborta chamadas anteriores
    if (inFlight.current) inFlight.current.abort();
    const ctrl = new AbortController();
    inFlight.current = ctrl;
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const d = await v360FeaturesAPI.get({ empresaId, overrideNivel, signal: ctrl.signal });
      if (ctrl.signal.aborted) return;
      setState({
        features:   d.features || {},
        nivel:      { codigo: d.nivel_codigo, nome: d.nivel_nome, emoji: d.nivel_emoji, cor: d.nivel_cor },
        is_default: !!d.is_default,
        loading:    false,
        error:      null,
      });
    } catch (e) {
      if (ctrl.signal.aborted) return;
      setState({ ...EMPTY, loading: false, error: e.message || "Falha ao carregar features V360." });
    }
  }, [empresaId, overrideNivel]);

  useEffect(() => { load(); }, [load]);

  const value = useMemo(() => ({
    ...state,
    empresaId,
    overrideNivel,
    setOverrideNivel: setManualOverride,
    reload: load,
  }), [state, empresaId, overrideNivel, load]);

  return <V360FeaturesContext.Provider value={value}>{children}</V360FeaturesContext.Provider>;
}


/** Hook — retorna o pacote de features + helpers.  */
export function useV360Features() {
  const ctx = useContext(V360FeaturesContext);
  // Se não estiver dentro do Provider, retorna um mock permissivo (fail-open).
  if (!ctx) return { ...EMPTY, loading: false, statusOf: () => "ATIVO", isAtivo: () => true, isDesabilitado: () => false };
  return {
    ...ctx,
    /** Retorna 'ATIVO' | 'PARCIAL' | 'DESABILITADO' para um code. Fallback ATIVO se não mapeado (fail-open). */
    statusOf: (code) => ctx.features?.[code]?.status || "ATIVO",
    isAtivo:        (code) => (ctx.features?.[code]?.status || "ATIVO") === "ATIVO",
    isParcial:      (code) => (ctx.features?.[code]?.status || "ATIVO") === "PARCIAL",
    isDesabilitado: (code) => (ctx.features?.[code]?.status || "ATIVO") === "DESABILITADO",
    descricaoOf:    (code) => ctx.features?.[code]?.descricao || "",
    subNomeOf:      (code) => ctx.features?.[code]?.sub_nome || ctx.features?.[code]?.area_nome || "",
  };
}
